import type { SearchWorkOrderCriteria, WorkOrderStatus, WorkOrderTypeFilter } from '@hetivo/hetivo-lib-types';
import { WORK_ORDER_STATUS, WORK_ORDER_TYPE_FILTER } from '@hetivo/hetivo-lib-types';
import { z } from 'zod/v4';

// ─── Tabs ───────────────────────────────────────────────────────────────────

/**
 * The work-orders list tabs, in display order (SWOOP-326, L2). The screen opens on **Open**; every tab
 * but `all` pins a single {@link WorkOrderStatus}. Work orders only reach `OPEN` today (WOW 1), so the
 * later-status tabs simply count 0 until WOW 2 (SWOOP-327/328).
 */
export const WORK_ORDER_TABS = ['all', 'open', 'received', 'atGarage', 'done', 'closed', 'cancelled'] as const;

export type WorkOrderTab = (typeof WORK_ORDER_TABS)[number];

/** The default tab the screen opens on (SWOOP-326, L2). */
export const DEFAULT_WORK_ORDER_TAB: WorkOrderTab = 'open';

/** Maps each non-`all` tab to the single status it pins; `all` sends no status filter. */
const TAB_STATUS: Record<Exclude<WorkOrderTab, 'all'>, WorkOrderStatus> = {
  atGarage: WORK_ORDER_STATUS.AT_GARAGE,
  cancelled: WORK_ORDER_STATUS.CANCELLED,
  closed: WORK_ORDER_STATUS.CLOSED,
  done: WORK_ORDER_STATUS.DONE,
  open: WORK_ORDER_STATUS.OPEN,
  received: WORK_ORDER_STATUS.RECEIVED,
};

/** The status a tab pins, or `undefined` for the `all` tab (SWOOP-326, L2). */
export function tabToStatus(tab: WorkOrderTab): WorkOrderStatus | undefined {
  return tab === 'all' ? undefined : TAB_STATUS[tab];
}

// ─── Filters ──────────────────────────────────────────────────────────────────

/**
 * The work-orders list filters (SWOOP-326, L3): the four-way **Type** filter and the **Garage** filter.
 * No location filter — the list follows the top territory / location selector only.
 */
export interface WorkOrderFilters {
  garageIds: string[];
  types: WorkOrderTypeFilter[];
}

export const EMPTY_WORK_ORDER_FILTERS: WorkOrderFilters = {
  garageIds: [],
  types: [],
};

// ─── URL search schema ──────────────────────────────────────────────────────────

const TYPE_FILTERS = Object.values(WORK_ORDER_TYPE_FILTER);
const stringArray = z.array(z.string()).optional().catch(undefined);

/**
 * The work-orders-list URL search-param schema (SWOOP-326). Lifting the tab / search / page / filters
 * into the URL lets a Back from a work order restore the list as it was. Every field is optional and
 * `.catch(undefined)`, so a hand-edited or stale URL degrades to the defaults instead of throwing, and a
 * plain navigation to `/work-orders` needs no search object.
 */
export const workOrdersListSearchSchema = z.object({
  garageIds: stringArray,
  page: z.number().int().positive().optional().catch(undefined),
  q: z.string().optional().catch(undefined),
  tab: z.enum(WORK_ORDER_TABS).optional().catch(undefined),
  types: stringArray,
  // The work order whose view drawer is open (SWOOP-305), so another screen (an issue's "View WO-…", or
  // the redirect after opening a work order from an issue) can link straight to it.
  view: z.string().optional().catch(undefined),
});

export type WorkOrdersListSearch = z.infer<typeof workOrdersListSearchSchema>;

// ─── Conversions ────────────────────────────────────────────────────────────────

/** Keeps only the known {@link WorkOrderTypeFilter} tokens from a raw string array. */
export function toTypes(values: string[] | undefined): WorkOrderTypeFilter[] {
  if (!values) {
    return [];
  }
  return values.filter((value): value is WorkOrderTypeFilter => (TYPE_FILTERS as string[]).includes(value));
}

/** Builds the display {@link WorkOrderFilters} object from the URL search params. */
export function searchToFilters(search: WorkOrdersListSearch): WorkOrderFilters {
  return {
    garageIds: search.garageIds ?? [],
    types: toTypes(search.types),
  };
}

/** `[]` collapses to `undefined` so the URL stays clean (no empty arrays). */
function arrayOrUndefined<T>(values: T[]): T[] | undefined {
  return values.length > 0 ? values : undefined;
}

/** Serialises the display {@link WorkOrderFilters} back into the URL search-param fields. */
export function filtersToSearch(filters: WorkOrderFilters): Pick<WorkOrdersListSearch, 'garageIds' | 'types'> {
  return {
    garageIds: arrayOrUndefined(filters.garageIds),
    types: arrayOrUndefined(filters.types),
  };
}

/**
 * The territory / location picked in the master selector (SWOOP-326, L3) — the list, its counts and the KPI
 * cards all follow it, like the other list pages. `locationId` is the selector's site.
 */
export interface WorkOrderScope {
  locationId?: string;
  territoryId?: string;
}

/** The selector scope as criteria fields (a territory is resolved to its locations on the server). */
export function scopeToCriteria(
  scope: WorkOrderScope | undefined,
): Pick<SearchWorkOrderCriteria, 'locationIds' | 'territoryIds'> {
  return {
    locationIds: scope?.locationId ? [scope.locationId] : undefined,
    territoryIds: scope?.territoryId ? [scope.territoryId] : undefined,
  };
}

/**
 * Builds the {@link SearchWorkOrderCriteria} for the given tab + filters + search. The master-selector
 * territory / location IS sent in the criteria (via {@link scopeToCriteria}); the backend scope guard
 * then applies the caller's own scope on top of it. Empty arrays are dropped so the request body stays
 * minimal.
 */
export function buildCriteria({
  filters,
  scope,
  search,
  tab,
}: {
  filters: WorkOrderFilters;
  scope?: WorkOrderScope;
  search: string | undefined;
  tab: WorkOrderTab;
}): SearchWorkOrderCriteria {
  const trimmed = search?.trim();
  return {
    ...scopeToCriteria(scope),
    garageIds: arrayOrUndefined(filters.garageIds),
    search: trimmed ? trimmed : undefined,
    status: tabToStatus(tab),
    types: arrayOrUndefined(filters.types),
  };
}

/** The same criteria as the list but without the status filter — what the counts endpoint reads (L2). */
export function buildCountsCriteria({
  filters,
  scope,
  search,
}: {
  filters: WorkOrderFilters;
  scope?: WorkOrderScope;
  search: string | undefined;
}): SearchWorkOrderCriteria {
  const trimmed = search?.trim();
  return {
    ...scopeToCriteria(scope),
    garageIds: arrayOrUndefined(filters.garageIds),
    search: trimmed ? trimmed : undefined,
    types: arrayOrUndefined(filters.types),
  };
}
